import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import Select from 'react-select';
import notifications from '../../notifications';
import { getProgramSessions, createProgramSessions, createOneOffProgramSession } from '../../api/admin';
import { rescheduleProgramSession, cancelProgramSession, substituteCoachInSession, updateSessionCoaches } from '../../api/programs';
import { markCoachLate } from '../../api/incentives';
import './SessionsTab.css';

const SessionsTab = ({ program, coaches }) => {
  const programId = program.id;
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedAction, setExpandedAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for add session
  const [showAddForm, setShowAddForm] = useState(false);
  const [addSessionDate, setAddSessionDate] = useState('');

  // Form state for reschedule
  const [rescheduleForm, setRescheduleForm] = useState({
    newDate: '',
    reason: '',
    lateNoticeFlag: false,
    coachIsResponsible: false,
    sendEmails: true
  });

  // Form state for cancel
  const [cancelForm, setCancelForm] = useState({
    reason: '',
    lateNoticeFlag: false,
    coachIsResponsible: false,
    sendEmails: true
  });

  // Form state for swap director
  const [swapForm, setSwapForm] = useState({
    newDirectorId: null,
    coachIsResponsible: false
  });

  // State for coach add/remove inline forms
  const [coachAction, setCoachAction] = useState(null); // { sessionId, type: 'add' | 'remove', coachId? }
  const [coachForm, setCoachForm] = useState({ selectedCoachId: null, propagate: false });

  // State for lateness inline form
  const [latenessAction, setLatenessAction] = useState(null); // { sessionId, coachId }
  const [latenessForm, setLatenessForm] = useState({ excused: false });

  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoading(true);
      try {
        const response = await getProgramSessions(programId);
        if (response.data) {
          const sortedSessions = (response.data || []).sort((a, b) =>
            a.date.localeCompare(b.date)
          );
          setSessions(sortedSessions);
        } else {
          notifications.notifyError('Failed to load sessions');
        }
      } catch (error) {
        notifications.notifyError('Failed to load sessions');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSessions();
  }, [programId]);

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const response = await getProgramSessions(programId);
      if (response.data) {
        const sortedSessions = (response.data || []).sort((a, b) =>
          a.date.localeCompare(b.date)
        );
        setSessions(sortedSessions);
      } else {
        notifications.notifyError('Failed to load sessions');
      }
    } catch (error) {
      notifications.notifyError('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpandAction = (sessionId, action) => {
    setCoachAction(null);
    setLatenessAction(null);
    if (expandedAction?.sessionId === sessionId && expandedAction?.action === action) {
      setExpandedAction(null);
    } else {
      setExpandedAction({ sessionId, action });
      // Reset form states
      setRescheduleForm({ newDate: '', reason: '', lateNoticeFlag: false, coachIsResponsible: false, sendEmails: true });
      setCancelForm({ reason: '', lateNoticeFlag: false, coachIsResponsible: false, sendEmails: true });
      setSwapForm({ newDirectorId: null, coachIsResponsible: false });
    }
  };

  const handleOpenCoachAction = (sessionId, type, coachId = null) => {
    setExpandedAction(null);
    setLatenessAction(null);
    if (coachAction?.sessionId === sessionId && coachAction?.type === type && coachAction?.coachId === coachId) {
      setCoachAction(null);
    } else {
      setCoachAction({ sessionId, type, coachId });
      setCoachForm({ selectedCoachId: null, propagate: false });
    }
  };

  const handleRescheduleSubmit = async (session) => {
    if (!rescheduleForm.newDate) {
      notifications.notifyError('New date is required');
      return;
    }
    if (rescheduleForm.sendEmails && !rescheduleForm.reason) {
      notifications.notifyError('Reason is required when sending emails');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await rescheduleProgramSession(programId, {
        date: session.date,
        ...rescheduleForm
      });
      if (!response.error) {
        notifications.notifySuccess('Session rescheduled successfully');
        setExpandedAction(null);
        loadSessions();
      } else {
        notifications.notifyError('Failed to reschedule session');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = async (session) => {
    if (cancelForm.sendEmails && !cancelForm.reason) {
      notifications.notifyError('Reason is required when sending emails');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await cancelProgramSession(programId, {
        date: session.date,
        ...cancelForm
      });
      if (!response.error) {
        notifications.notifySuccess('Session cancelled successfully');
        setExpandedAction(null);
        loadSessions();
      } else {
        notifications.notifyError('Failed to cancel session');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwapSubmit = async (session) => {
    if (!swapForm.newDirectorId) {
      notifications.notifyError('New director is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await substituteCoachInSession({
        programSessionId: session.id,
        oldCoachId: session.director,
        newCoachId: swapForm.newDirectorId,
        coachIsResponsible: swapForm.coachIsResponsible
      });
      if (!response.error) {
        notifications.notifySuccess('Director swapped successfully');
        setExpandedAction(null);
        loadSessions();
      } else {
        notifications.notifyError('Failed to swap director');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCoachActionSubmit = async () => {
    if (coachAction.type === 'add' && !coachForm.selectedCoachId) {
      notifications.notifyError('Please select a coach to add');
      return;
    }
    const add = coachAction.type === 'add' ? [coachForm.selectedCoachId] : [];
    const remove = coachAction.type === 'remove' ? [coachAction.coachId] : [];
    setIsSubmitting(true);
    try {
      const response = await updateSessionCoaches(coachAction.sessionId, {
        add,
        remove,
        propagate: coachForm.propagate
      });
      if (!response.error) {
        notifications.notifySuccess(coachAction.type === 'add' ? 'Coach added successfully' : 'Coach removed successfully');
        setCoachAction(null);
        loadSessions();
      } else {
        notifications.notifyError(`Failed to ${coachAction.type} coach`);
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenLatenessAction = (sessionId, coachId) => {
    setExpandedAction(null);
    setCoachAction(null);
    if (latenessAction?.sessionId === sessionId && latenessAction?.coachId === coachId) {
      setLatenessAction(null);
    } else {
      setLatenessAction({ sessionId, coachId });
      setLatenessForm({ excused: false });
    }
  };

  const handleLatenessSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await markCoachLate(latenessAction.sessionId, latenessAction.coachId, { excused: latenessForm.excused });
      if (!response.error) {
        notifications.notifySuccess('Coach marked as late');
        setLatenessAction(null);
        loadSessions();
      } else {
        notifications.notifyError('Failed to mark coach as late');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAttendanceBadge = (session, coachId) => {
    const attendance = session.coachAttendance?.[coachId];
    if (!attendance) {
      return { label: 'On time', style: { background: '#fff', color: '#666', border: '1px solid #ccc' } };
    }
    if (!attendance.attended && attendance.excused) {
      return { label: 'Excused', style: { background: '#fff', color: '#1976d2', border: '1px solid #1976d2' } };
    }
    if (!attendance.attended && !attendance.excused) {
      return { label: 'Absent', style: { background: '#fff', color: '#c62828', border: '1px solid #c62828' } };
    }
    if (attendance.attended && attendance.late && attendance.excused) {
      return { label: 'Late (excused)', style: { background: '#fff', color: '#1976d2', border: '1px solid #1976d2' } };
    }
    if (attendance.attended && attendance.late) {
      return { label: 'Late', style: { background: '#fff', color: '#f57c00', border: '1px solid #f57c00' } };
    }
    return { label: 'On time', style: { background: '#fff', color: '#666', border: '1px solid #ccc' } };
  };

  const handleCreateSessions = async () => {
    setIsSubmitting(true);
    try {
      const response = await createProgramSessions(programId);
      if (!response.error) {
        notifications.notifySuccess('Sessions created successfully');
        loadSessions();
      } else {
        notifications.notifyError('Failed to create sessions');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSessionSubmit = async () => {
    if (!addSessionDate) {
      notifications.notifyError('Date is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await createOneOffProgramSession(programId, addSessionDate);
      if (!response.error) {
        notifications.notifySuccess('Session added successfully');
        setShowAddForm(false);
        setAddSessionDate('');
        loadSessions();
      } else {
        notifications.notifyError('Failed to add session');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canCreateSessions = () => {
    return (
      program.director &&
      (program.status === 'confirmed' || program.status === 'active') &&
      sessions.length === 0
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return DateTime.fromISO(dateStr).toFormat('EEE, MMM d, yyyy');
  };

  const formatTime = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    return `${startTime} - ${endTime}`;
  };

  const getCoachName = (coachId) => {
    if (!coachId || !coaches) return '-';
    const coach = coaches.find(c => c.id === coachId);
    return coach ? `${coach.firstName} ${coach.lastName}` : '-';
  };

  const isPastSession = (dateStr) => {
    if (!dateStr) return false;
    const sessionDate = DateTime.fromISO(dateStr);
    const today = DateTime.now().startOf('day');
    return sessionDate < today;
  };

  if (isLoading) {
    return <div className="sessions-loading">Loading sessions...</div>;
  }

  return (
    <div className="sessions-tab">
      <div className="sessions-header">
        <h3>Sessions ({sessions.length})</h3>
        <button className="add-session-btn" onClick={() => setShowAddForm(!showAddForm)}>
          + Add Session
        </button>
      </div>

      {showAddForm && (
        <div className="inline-form add-session-form">
          <h4>Add Session</h4>
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={addSessionDate}
              onChange={(e) => setAddSessionDate(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => { setShowAddForm(false); setAddSessionDate(''); }} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="button" className="submit-btn" onClick={handleAddSessionSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Session'}
            </button>
          </div>
        </div>
      )}

      <div className="sessions-table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Director</th>
              <th>Coaches</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => {
              const past = isPastSession(session.date);
              const canceled = !!session.canceled;
              const sessionCoaches = session.coaches || [];
              return (
                <React.Fragment key={session.id}>
                  <tr className={`${past ? 'past-session' : ''}${canceled ? ' canceled-session' : ''}`}>
                    <td>
                      {formatDate(session.date)}
                      {canceled && (
                        <span className="session-badge session-badge--cancelled">
                          Cancelled{session.coachFault ? ' · Coach no-show' : ''}
                        </span>
                      )}
                    </td>
                    <td>{formatTime(session.startTime, session.endTime)}</td>
                    <td>{getCoachName(session.director)}</td>
                    <td className="coaches-cell">
                      {sessionCoaches.map((coachId) => (
                        <span key={coachId} className="coach-chip">
                          {getCoachName(coachId)}
                          {!past && (
                            <button
                              className="remove-btn"
                              onClick={() => handleOpenCoachAction(session.id, 'remove', coachId)}
                              title="Remove coach"
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))}
                      {!past && (
                        <button
                          className={`add-coach-btn ${coachAction?.sessionId === session.id && coachAction?.type === 'add' ? 'active' : ''}`}
                          onClick={() => handleOpenCoachAction(session.id, 'add')}
                          title="Add coach"
                        >
                          +
                        </button>
                      )}
                    </td>
                    <td className="actions-cell">
                      {canceled ? (
                        <span className="canceled-note">—</span>
                      ) : (
                        <>
                      {session.director && (
                        <button
                          className={`action-btn swap ${expandedAction?.sessionId === session.id && expandedAction?.action === 'swap' ? 'active' : ''}`}
                          onClick={() => handleExpandAction(session.id, 'swap')}
                        >
                          Swap Director
                        </button>
                      )}
                      {!past && (
                        <>
                          <button
                            className={`action-btn reschedule ${expandedAction?.sessionId === session.id && expandedAction?.action === 'reschedule' ? 'active' : ''}`}
                            onClick={() => handleExpandAction(session.id, 'reschedule')}
                          >
                            Reschedule
                          </button>
                          <button
                            className={`action-btn cancel ${expandedAction?.sessionId === session.id && expandedAction?.action === 'cancel' ? 'active' : ''}`}
                            onClick={() => handleExpandAction(session.id, 'cancel')}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {past && (
                        <>
                          {[session.director, ...(session.coaches || [])].filter(Boolean).map((coachId) => {
                            const badge = getAttendanceBadge(session, coachId);
                            const isActive = latenessAction?.sessionId === session.id && latenessAction?.coachId === coachId;
                            return (
                              <button
                                key={coachId}
                                className={`performance-badge${isActive ? ' active' : ''}`}
                                style={badge.style}
                                onClick={() => handleOpenLatenessAction(session.id, coachId)}
                                title={`${getCoachName(coachId)}: ${badge.label}`}
                              >
                                {getCoachName(coachId)}: {badge.label}
                              </button>
                            );
                          })}
                        </>
                      )}
                        </>
                      )}
                    </td>
                  </tr>
                  {expandedAction?.sessionId === session.id && (
                    <tr className="action-row">
                      <td colSpan="5">
                        {expandedAction.action === 'reschedule' && (
                          <div className="inline-form reschedule-form">
                            <h4>Reschedule Session</h4>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Current Date</label>
                                <input type="date" value={session.date} disabled />
                              </div>
                              <div className="form-group">
                                <label>New Date *</label>
                                <input
                                  type="date"
                                  value={rescheduleForm.newDate}
                                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, newDate: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={rescheduleForm.sendEmails}
                                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, sendEmails: e.target.checked })}
                                />
                                Send notification emails to parents
                              </label>
                            </div>
                            {rescheduleForm.sendEmails && (
                              <>
                                <div className="form-group">
                                  <label>Reason *</label>
                                  <textarea
                                    value={rescheduleForm.reason}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, reason: e.target.value })}
                                    placeholder="Reason for rescheduling..."
                                    rows={2}
                                  />
                                </div>
                                <div className="checkbox-row">
                                  <label className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={rescheduleForm.lateNoticeFlag}
                                      onChange={(e) => setRescheduleForm({ ...rescheduleForm, lateNoticeFlag: e.target.checked })}
                                    />
                                    <span>
                                      Late Notice
                                      <span className="checkbox-subtext">adds an apology for the short notice to the email</span>
                                    </span>
                                  </label>
                                </div>
                              </>
                            )}
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={rescheduleForm.coachIsResponsible}
                                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, coachIsResponsible: e.target.checked })}
                                />
                                <span>
                                  Coach no-show
                                  <span className="checkbox-subtext">unexcused absence — disqualifies from program bonus</span>
                                </span>
                              </label>
                            </div>
                            <div className="form-actions">
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setExpandedAction(null)}
                                disabled={isSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="submit-btn"
                                onClick={() => handleRescheduleSubmit(session)}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? 'Saving...' : 'Reschedule Session'}
                              </button>
                            </div>
                          </div>
                        )}
                        {expandedAction.action === 'cancel' && (
                          <div className="inline-form cancel-form">
                            <h4>Cancel Session - {formatDate(session.date)}</h4>
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={cancelForm.sendEmails}
                                  onChange={(e) => setCancelForm({ ...cancelForm, sendEmails: e.target.checked })}
                                />
                                Send notification emails to parents
                              </label>
                            </div>
                            {cancelForm.sendEmails && (
                              <>
                                <div className="form-group">
                                  <label>Reason *</label>
                                  <textarea
                                    value={cancelForm.reason}
                                    onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                                    placeholder="Reason for cancellation..."
                                    rows={2}
                                  />
                                </div>
                                <div className="checkbox-row">
                                  <label className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={cancelForm.lateNoticeFlag}
                                      onChange={(e) => setCancelForm({ ...cancelForm, lateNoticeFlag: e.target.checked })}
                                    />
                                    <span>
                                      Late Notice
                                      <span className="checkbox-subtext">adds an apology for the short notice to the email</span>
                                    </span>
                                  </label>
                                </div>
                              </>
                            )}
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={cancelForm.coachIsResponsible}
                                  onChange={(e) => setCancelForm({ ...cancelForm, coachIsResponsible: e.target.checked })}
                                />
                                <span>
                                  Coach no-show
                                  <span className="checkbox-subtext">unexcused absence — disqualifies from program bonus</span>
                                </span>
                              </label>
                            </div>
                            <div className="form-actions">
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setExpandedAction(null)}
                                disabled={isSubmitting}
                              >
                                Back
                              </button>
                              <button
                                type="button"
                                className="submit-btn danger"
                                onClick={() => handleCancelSubmit(session)}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? 'Cancelling...' : 'Cancel Session'}
                              </button>
                            </div>
                          </div>
                        )}
                        {expandedAction.action === 'swap' && (
                          <div className="inline-form swap-form">
                            <h4>Swap Director - {formatDate(session.date)}</h4>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Current Director</label>
                                <input type="text" value={getCoachName(session.director)} disabled />
                              </div>
                              <div className="form-group">
                                <label>New Director *</label>
                                <Select
                                  options={coaches
                                    ?.filter(c => c.id !== session.director && c.status === 'active')
                                    .map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
                                  value={swapForm.newDirectorId ? {
                                    value: swapForm.newDirectorId,
                                    label: getCoachName(swapForm.newDirectorId)
                                  } : null}
                                  onChange={(option) => setSwapForm({ ...swapForm, newDirectorId: option?.value || null })}
                                  placeholder="Select new director..."
                                  isClearable
                                />
                              </div>
                            </div>
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={swapForm.coachIsResponsible}
                                  onChange={(e) => setSwapForm({ ...swapForm, coachIsResponsible: e.target.checked })}
                                />
                                <span>
                                  Outgoing coach didn't communicate ahead
                                  <span className="checkbox-subtext">unexcused absence — disqualifies from program bonus</span>
                                </span>
                              </label>
                            </div>
                            <div className="form-actions">
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setExpandedAction(null)}
                                disabled={isSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="submit-btn"
                                onClick={() => handleSwapSubmit(session)}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? 'Swapping...' : 'Swap Director'}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {coachAction?.sessionId === session.id && (
                    <tr className="action-row">
                      <td colSpan="5">
                        {coachAction.type === 'remove' && (
                          <div className="inline-form coach-form">
                            <h4>Remove {getCoachName(coachAction.coachId)} from this session?</h4>
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={coachForm.propagate}
                                  onChange={(e) => setCoachForm({ ...coachForm, propagate: e.target.checked })}
                                />
                                Also apply to all future sessions
                              </label>
                            </div>
                            <div className="form-actions">
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setCoachAction(null)}
                                disabled={isSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="submit-btn danger"
                                onClick={handleCoachActionSubmit}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? 'Removing...' : 'Remove'}
                              </button>
                            </div>
                          </div>
                        )}
                        {coachAction.type === 'add' && (
                          <div className="inline-form coach-form">
                            <h4>Add Coach - {formatDate(session.date)}</h4>
                            <div className="form-group">
                              <Select
                                options={coaches
                                  ?.filter(c => c.status === 'active' && c.id !== session.director && !(session.coaches || []).includes(c.id))
                                  .map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
                                value={coachForm.selectedCoachId ? {
                                  value: coachForm.selectedCoachId,
                                  label: getCoachName(coachForm.selectedCoachId)
                                } : null}
                                onChange={(option) => setCoachForm({ ...coachForm, selectedCoachId: option?.value || null })}
                                placeholder="Select coach..."
                                isClearable
                              />
                            </div>
                            <div className="checkbox-row">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={coachForm.propagate}
                                  onChange={(e) => setCoachForm({ ...coachForm, propagate: e.target.checked })}
                                />
                                Also apply to all future sessions
                              </label>
                            </div>
                            <div className="form-actions">
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setCoachAction(null)}
                                disabled={isSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="submit-btn"
                                onClick={handleCoachActionSubmit}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? 'Adding...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {latenessAction?.sessionId === session.id && (
                    <tr className="action-row">
                      <td colSpan="5">
                        <div className="inline-form lateness-form">
                          <h4>Mark Late — {getCoachName(latenessAction.coachId)}</h4>
                          <div className="form-group">
                            <label>Coach</label>
                            <input type="text" value={getCoachName(latenessAction.coachId)} disabled />
                          </div>
                          <div className="checkbox-row">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={latenessForm.excused}
                                onChange={(e) => setLatenessForm({ ...latenessForm, excused: e.target.checked })}
                              />
                              Excused?
                            </label>
                          </div>
                          <div className="form-actions">
                            <button
                              type="button"
                              className="cancel-btn"
                              onClick={() => setLatenessAction(null)}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="submit-btn"
                              onClick={handleLatenessSubmit}
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? 'Saving...' : 'Mark Late'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {sessions.length === 0 && (
          <div className="empty-sessions">
            <p>No sessions created for this program</p>
            {canCreateSessions() && (
              <button
                className="create-sessions-btn"
                onClick={handleCreateSessions}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Sessions'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsTab;
